import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Loader2, Lock, Ban, Clock, Activity } from 'lucide-react';
import { gsap } from 'gsap';

import Header from '../components/Header.jsx';
import Footer from '../components/Footer.jsx';
import { Meta, breadcrumbLd } from '../seo/Meta.jsx';
import { useLenis } from '../anim/useLenis.js';
import generatedPortals from '../data/portals-generated.json';

// Coverage matrix.
//
// Reads two sources:
//   1. docs/coverage/portals.yaml (build-time, via gen-portals.js)
//      - country, country_name, slug, name, status (wip/live/blocked-tos/...)
//   2. /v1/status (runtime) - per-component uptime + last check from the
//      status-checker worker.
//
// We display the union: every portal from yaml plus an "uptime" column
// hydrated from /v1/status when the slug matches a status component.
export default function Coverage() {
  useLenis();
  const [statusByComponent, setStatusByComponent] = useState({});

  useEffect(() => {
    fetch('/v1/status', { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!d?.components) return;
        const m = {};
        for (const c of d.components) m[c.slug] = c;
        setStatusByComponent(m);
      })
      .catch(() => {});
  }, []);

  const rows = (generatedPortals.portals || []).filter((p) => p.slug);
  const summary = summarize(rows);

  return (
    <>
      <Meta
        title="Coverage matrix - tender-api.com"
        description="Live coverage matrix across procurement portals. Pulled from docs/coverage/portals.yaml and live status checks."
        path="/coverage"
        jsonLd={[
          breadcrumbLd([
            { name: 'tender-api', path: '/' },
            { name: 'Coverage', path: '/coverage' },
          ]),
        ]}
      />
      <Header />
      <main className="container" style={{ paddingTop: 64, paddingBottom: 96 }}>
        <h1 className="hero-headline mono" style={{ fontSize: 'clamp(36px, 5vw, 60px)' }}>
          Coverage matrix
        </h1>
        <p className="lede" style={{ maxWidth: 760, marginTop: 16 }}>
          Live, never cached. Every portal here is wired to the official state-owned
          source. License brief enforced before any row flips to <code>live</code>.
        </p>

        <div style={statGrid}>
          <Stat label="Portals tracked" value={rows.length} />
          <Stat label="Live" value={summary.live} accent="var(--accent)" />
          <Stat label="In progress" value={summary.wip} />
          <Stat label="Blocked / not-yet" value={summary.blocked + summary.none} />
        </div>

        <div style={{ marginTop: 48, overflowX: 'auto' }}>
          <table style={tbl}>
            <thead>
              <tr>
                <Th>Country</Th>
                <Th>Portal</Th>
                <Th>Slug</Th>
                <Th>Cadence</Th>
                <Th>Sub-threshold</Th>
                <Th>Uptime 30d</Th>
                <Th>Status</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p, i) => {
                const live = statusByComponent[p.slug];
                return (
                  <motion.tr
                    key={`${p.slug}-${i}`}
                    initial={{ opacity: 0, y: 6 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: Math.min(i * 0.015, 0.4) }}
                    style={trStyle}
                  >
                    <Td>
                      <span>{p.country_name || p.country}</span>
                      <span className="mono" style={{ color: 'var(--fg-3)', marginLeft: 6, fontSize: 11 }}>
                        {p.country}
                      </span>
                    </Td>
                    <Td>{p.name || '...'}</Td>
                    <Td mono>{p.slug}</Td>
                    <Td muted mono>{p.cadence || '...'}</Td>
                    <Td muted>{p.sub_threshold ? 'yes' : '-'}</Td>
                    <Td mono>
                      {live ? (
                        <UptimeBadge pct={live.uptime_pct_30d} status={live.last_status} />
                      ) : (
                        <span style={{ color: 'var(--fg-3)' }}>...</span>
                      )}
                    </Td>
                    <Td><StatusPill status={p.status} /></Td>
                  </motion.tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <p style={{ marginTop: 32, fontSize: 13, color: 'var(--fg-3)', maxWidth: 720 }}>
          Source-of-truth: <code>docs/coverage/portals.yaml</code> in the public repo.
          Uptime column reflects the last 30 days of status-checker pings (see
          <a href="/status" style={{ color: 'var(--accent)', marginLeft: 4 }}>status page</a>).
        </p>
      </main>
      <Footer />
    </>
  );
}

const statGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
  gap: 16,
  marginTop: 48,
};

const tbl = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: 14,
};

const trStyle = { borderBottom: '1px solid var(--line-1)' };

function Th({ children }) {
  return (
    <th style={{
      textAlign: 'left',
      padding: '10px 12px',
      fontSize: 12,
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
      color: 'var(--fg-3)',
      fontWeight: 600,
      borderBottom: '1px solid var(--line-2)',
    }}>{children}</th>
  );
}

function Td({ children, mono, muted, align = 'left' }) {
  return (
    <td style={{
      padding: '10px 12px',
      textAlign: align,
      fontFamily: mono ? 'var(--font-mono)' : 'inherit',
      color: muted ? 'var(--fg-3)' : 'var(--fg-1)',
      verticalAlign: 'middle',
    }}>{children}</td>
  );
}

function StatusPill({ status }) {
  const st = String(status || 'wip');
  const variants = {
    live:           { bg: 'rgba(255,95,31,0.12)', fg: 'var(--accent)', label: 'live', Icon: CheckCircle2 },
    wip:            { bg: 'rgba(0,0,0,0.05)', fg: 'var(--fg-2)', label: 'wip', Icon: Clock },
    'blocked-tos':  { bg: 'rgba(220,38,38,0.10)', fg: '#dc2626', label: 'blocked tos', Icon: Lock },
    'blocked-sanctions': { bg: 'rgba(220,38,38,0.10)', fg: '#dc2626', label: 'sanctions', Icon: Ban },
    none:           { bg: 'rgba(0,0,0,0.05)', fg: 'var(--fg-3)', label: 'none', Icon: Ban },
  };
  const v = variants[st] || variants.wip;
  const I = v.Icon;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '3px 10px', borderRadius: 999, fontSize: 11, fontWeight: 600,
      background: v.bg, color: v.fg, fontFamily: 'var(--font-mono)',
    }}>
      <I size={11} strokeWidth={2.5} />
      {v.label}
    </span>
  );
}

function UptimeBadge({ pct, status }) {
  const color = status === 'up'
    ? 'var(--accent)'
    : status === 'degraded'
      ? '#f59e0b'
      : '#dc2626';
  return (
    <span style={{ color, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <Activity size={11} />
      {Math.round(pct ?? 0)}%
    </span>
  );
}

function Stat({ label, value, accent }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    const obj = { v: 0 };
    gsap.to(obj, {
      v: value,
      duration: 1.2,
      ease: 'expo.out',
      onUpdate() {
        if (!ref.current) return;
        ref.current.textContent = Math.round(obj.v).toLocaleString('en-CH');
      },
    });
  }, [value]);
  return (
    <motion.div
      className="stat-tile"
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, ease: [0.7, 0, 0.15, 1] }}
      style={{
        background: 'var(--bg-1)',
        border: '1px solid var(--line-2)',
        borderRadius: 8,
        padding: 20,
      }}
    >
      <div style={{
        fontSize: 12, letterSpacing: '0.04em', textTransform: 'uppercase',
        color: 'var(--fg-3)', fontFamily: 'var(--font-mono)', marginBottom: 8,
      }}>{label}</div>
      <div style={{
        fontSize: 32, fontWeight: 700, fontFamily: 'var(--font-mono)',
        color: accent || 'var(--fg-0)',
      }} ref={ref}>0</div>
    </motion.div>
  );
}

function summarize(rows) {
  const out = { live: 0, wip: 0, 'blocked-tos': 0, 'blocked-sanctions': 0, blocked: 0, none: 0 };
  for (const r of rows) {
    const s = String(r.status || 'wip');
    if (s.startsWith('blocked')) out.blocked++;
    else out[s] = (out[s] || 0) + 1;
  }
  return out;
}
